<template>
  <div class="stage-bg" aria-hidden="true">
    <div ref="scene" class="scene scene-routes">
      <div class="routes-glow"></div>

      <div
        v-for="band in bands"
        :key="band.key"
        :class="['wave-layer', `wave-${band.key}`]"
      >
        <svg viewBox="0 0 2880 320" preserveAspectRatio="none" aria-hidden="true">
          <g><path :class="`wave-band wave-band--${band.key}`" :d="band.d" /></g>
        </svg>
      </div>

      <canvas ref="canvas" class="fleet-canvas"></canvas>
    </div>

    <div class="stage-light"></div>
    <div class="stage-pulse"></div>
  </div>
</template>

<script>
import {
  FLEET, routePoint, stepShip, linkAlpha, createFleet,
} from '@/util/auth/fleetRoutes';
import { pulseValue } from '@/util/auth/authIntro';

const TAU = Math.PI * 2;
const MOTION_LERP = 0.045;
const POINTER_LERP = 0.08;
const PULSE_MS = 900;
const RIPPLE_GROWTH = 3.8;
const RIPPLE_DECAY = 0.008;
const LINK_MIN = 180;
const LINK_MAX = 380;

// satu periode gelombang = 720 unit viewBox, dipakai empat kali biar 2880 utuh
const BANDS = [
  { key: 'back', d: 'M0,160 c120,-52 240,-52 360,0 c120,52 240,52 360,0 c120,-52 240,-52 360,0 c120,52 240,52 360,0 c120,-52 240,-52 360,0 c120,52 240,52 360,0 c120,-52 240,-52 360,0 c120,52 240,52 360,0 L2880,320 L0,320 Z' },
  { key: 'mid', d: 'M0,150 c120,-58 240,-58 360,0 c120,58 240,58 360,0 c120,-58 240,-58 360,0 c120,58 240,58 360,0 c120,-58 240,-58 360,0 c120,58 240,58 360,0 c120,-58 240,-58 360,0 c120,58 240,58 360,0 L2880,320 L0,320 Z' },
  { key: 'front', d: 'M0,140 c120,-44 240,-44 360,0 c120,44 240,44 360,0 c120,-44 240,-44 360,0 c120,44 240,44 360,0 c120,-44 240,-44 360,0 c120,44 240,44 360,0 c120,-44 240,-44 360,0 c120,44 240,44 360,0 L2880,320 L0,320 Z' },
];

// cadangan kalau token temanya belum kebaca; nilai hidupnya dari auth-stage.css
const FALLBACK_PALETTE = {
  accent: [217, 64, 127],
  wake: [73, 130, 203],
  link: [45, 93, 178],
  hull: [27, 58, 107],
  warn: [231, 76, 60],
};

function lerp(from, to, amount) { return from + (to - from) * amount; }

function clamp(value, min, max) { return Math.min(Math.max(value, min), max); }

function readParts(style, name, target) {
  const raw = style.getPropertyValue(name).split(',');
  if (raw.length !== 3) return;
  raw.forEach((piece, index) => {
    const value = parseInt(piece, 10);
    if (!Number.isNaN(value)) target[index] = value;
  });
}

export default {
  name: 'AuthBackground',
  props: {
    motion: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    bands() { return BANDS; },
  },
  created() {
    // state kanvas sengaja di luar data() biar Vue gak bikin reaktif per titik jejak
    this.ctx = null;
    this.ships = [];
    this.lanePaths = [];
    this.ripples = [];
    this.frameId = 0;
    this.destroyed = false;
    this.tick = 0;
    this.lead = -1;
    this.width = 0;
    this.height = 0;
    this.ratio = 1;
    this.linkDistance = LINK_MIN;
    this.stageRect = {
      left: 0, top: 0, width: 1, height: 1,
    };
    this.pointerX = 0.5;
    this.pointerY = 0.5;
    this.pointerTargetX = 0.5;
    this.pointerTargetY = 0.5;
    this.pointerActive = false;
    this.bgMotion = 1;
    this.lastRate = 1;
    this.pulseLevel = 0;
    this.pulseStartedAt = 0;
    this.pulsePlaying = false;
    this.palette = {
      accent: [...FALLBACK_PALETTE.accent],
      wake: [...FALLBACK_PALETTE.wake],
      link: [...FALLBACK_PALETTE.link],
      hull: [...FALLBACK_PALETTE.hull],
      warn: [...FALLBACK_PALETTE.warn],
    };
    this.pulseParts = this.palette.warn;
    this.inkScale = 1;
    this.glowScale = 1;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },
  mounted() {
    const canvas = this.$refs.canvas;
    this.ctx = canvas ? canvas.getContext('2d') : null;
    this.syncPalette();

    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('pointermove', this.onPointerMove, { passive: true });
    document.addEventListener('pointerleave', this.onPointerLeave);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    if (canvas) canvas.addEventListener('pointerdown', this.onPointerDown);

    this.start();
  },
  beforeDestroy() {
    this.destroyed = true;
    this.stop();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerleave', this.onPointerLeave);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    const canvas = this.$refs.canvas;
    if (canvas) canvas.removeEventListener('pointerdown', this.onPointerDown);
  },
  methods: {
    /* ---------- palette ---------- */

    syncPalette() {
      if (!this.$el) return;
      const style = window.getComputedStyle(this.$el);
      // isinya ditimpa di tempat, jadi acuan yang udah dipegang tetap sah
      readParts(style, '--fleet-accent-rgb', this.palette.accent);
      readParts(style, '--fleet-wake-rgb', this.palette.wake);
      readParts(style, '--fleet-link-rgb', this.palette.link);
      readParts(style, '--fleet-hull-rgb', this.palette.hull);
      readParts(style, '--fleet-warn-rgb', this.palette.warn);
      const alpha = parseFloat(style.getPropertyValue('--fleet-alpha'));
      this.inkScale = alpha > 0 ? alpha : 1;
      const glow = parseFloat(style.getPropertyValue('--fleet-glow'));
      this.glowScale = glow > 0 ? glow : 1;
    },

    mixParts(from, to, amount) {
      return `${Math.round(from[0] + (to[0] - from[0]) * amount)}, `
        + `${Math.round(from[1] + (to[1] - from[1]) * amount)}, `
        + `${Math.round(from[2] + (to[2] - from[2]) * amount)}`;
    },

    // warna latar kegeser ke warna denyut selama denyutnya hidup
    hotTint() { return this.mixParts(this.palette.accent, this.pulseParts, this.pulseLevel); },
    coolTint() { return this.mixParts(this.palette.link, this.pulseParts, this.pulseLevel * 0.75); },
    wakeTint() { return this.mixParts(this.palette.wake, this.pulseParts, this.pulseLevel * 0.75); },
    hullTint() { return this.mixParts(this.palette.hull, this.pulseParts, this.pulseLevel * 0.6); },

    // tema terang butuh goresan lebih pekat buat jarak baca yang sama
    ink(value, scale) {
      const amount = value * this.inkScale * (scale === undefined ? 1 : scale);
      return (amount > 1 ? 1 : amount).toFixed(3);
    },

    /* ---------- denyut ---------- */

    pulse(tone) {
      if (this.reduceMotion || !this.$el) return;
      this.pulseParts = tone === 'error' ? this.palette.warn : this.palette.accent;
      this.$el.style.setProperty('--pulse-rgb', this.pulseParts.join(', '));
      this.pulseStartedAt = performance.now();
      this.pulsePlaying = true;
    },

    advancePulse() {
      if (!this.pulsePlaying) return;
      const elapsed = performance.now() - this.pulseStartedAt;
      if (elapsed >= PULSE_MS) {
        this.pulsePlaying = false;
        this.pulseLevel = 0;
        // nilai sisipannya dibuang biar warna denyut balik ikut tema
        this.$el.style.removeProperty('--pulse-rgb');
      } else {
        this.pulseLevel = pulseValue(elapsed, PULSE_MS);
      }
      this.$el.style.setProperty('--pulse', this.pulseLevel.toFixed(3));
    },

    /* ---------- ukuran & pointer ---------- */

    measureStage() {
      const box = this.$el.getBoundingClientRect();
      this.stageRect.left = box.left;
      this.stageRect.top = box.top;
      this.stageRect.width = box.width || 1;
      this.stageRect.height = box.height || 1;
    },

    onScroll() { this.measureStage(); },

    onResize() {
      this.measureStage();
      this.resizeCanvas();
      if (this.ships.length) this.primeTrails();
      if (this.reduceMotion) this.stepCanvas();
    },

    onPointerMove(event) {
      this.pointerTargetX = (event.clientX - this.stageRect.left) / this.stageRect.width;
      this.pointerTargetY = (event.clientY - this.stageRect.top) / this.stageRect.height;
      this.pointerActive = true;
    },

    onPointerLeave() {
      this.pointerTargetX = 0.5;
      this.pointerTargetY = 0.5;
      this.pointerActive = false;
    },

    onPointerDown(event) {
      if (this.reduceMotion) return;
      this.ripples.push({
        x: event.clientX - this.stageRect.left,
        y: event.clientY - this.stageRect.top,
        radius: 8,
        life: 1,
      });
    },

    onVisibilityChange() {
      if (document.hidden) this.stop(); else this.start();
    },

    /* ---------- loop ---------- */

    start() {
      if (this.destroyed || this.frameId || !this.ctx) return;
      this.measureStage();
      this.resizeCanvas();
      if (!this.ships.length) this.seed();
      this.driveVariables();
      this.stepCanvas();
      if (this.reduceMotion) return;
      this.frameId = requestAnimationFrame(this.loop);
    },

    stop() {
      if (!this.frameId) return;
      cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    },

    loop() {
      this.frameId = requestAnimationFrame(this.loop);
      this.driveVariables();
      this.stepCanvas();
    },

    driveVariables() {
      this.pointerX = lerp(this.pointerX, this.pointerTargetX, POINTER_LERP);
      this.pointerY = lerp(this.pointerY, this.pointerTargetY, POINTER_LERP);
      this.$el.style.setProperty('--mx', ((this.pointerX - 0.5) * 2).toFixed(4));
      this.$el.style.setProperty('--my', ((this.pointerY - 0.5) * 2).toFixed(4));
      this.bgMotion = lerp(this.bgMotion, this.motion, MOTION_LERP);
      this.applyCssPlaybackRate();
      this.advancePulse();
    },

    // gelombang dan cahaya CSS dilambatin lewat playbackRate biar gak melompat
    applyCssPlaybackRate() {
      if (Math.abs(this.bgMotion - this.lastRate) < 0.02) return;
      this.lastRate = this.bgMotion;
      const scene = this.$refs.scene;
      if (!scene || !scene.getAnimations) return;
      scene.getAnimations({ subtree: true }).forEach((animation) => {
        try {
          animation.updatePlaybackRate(this.bgMotion);
        } catch (error) {
          animation.playbackRate = this.bgMotion;
        }
      });
    },

    /* ---------- armada ---------- */

    seed() {
      this.ships = createFleet(Math.random).map((ship) => ({
        ...ship, x: 0, y: 0, angle: 0,
      }));
      this.buildLanePaths();
      this.primeTrails();
    },

    scaledPoint(route, t) {
      const point = routePoint(route, t);
      point.x *= this.width;
      point.y *= this.height;
      return point;
    },

    // jalurnya tetap dalam satuan 0..1, jadi cukup dibangun ulang saat ukurannya berubah
    buildLanePaths() {
      this.lanePaths = this.ships.map((ship) => {
        const path = new Path2D();
        for (let i = 0; i <= FLEET.LANE_SAMPLES; i += 1) {
          const point = this.scaledPoint(ship.route, i / FLEET.LANE_SAMPLES);
          if (i === 0) path.moveTo(point.x, point.y); else path.lineTo(point.x, point.y);
        }
        return path;
      });
    },

    primeTrails() {
      this.ships.forEach((ship) => {
        ship.trail.length = 0;
        for (let i = FLEET.TRAIL_MAX - 1; i >= 0; i -= 1) {
          const back = ship.t - ship.speed * FLEET.TRAIL_EVERY * i;
          if (back >= 0) ship.trail.push(this.scaledPoint(ship.route, back));
        }
      });
    },

    resizeCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.ctx) return;
      this.ratio = Math.min(window.devicePixelRatio || 1, 2);
      this.width = this.stageRect.width;
      this.height = this.stageRect.height;
      canvas.width = Math.round(this.width * this.ratio);
      canvas.height = Math.round(this.height * this.ratio);
      this.ctx.setTransform(this.ratio, 0, 0, this.ratio, 0, 0);
      this.linkDistance = clamp(Math.sqrt(this.width * this.height) / 3, LINK_MIN, LINK_MAX);
      if (this.ships.length) this.buildLanePaths();
    },

    advanceRipples() {
      this.ripples = this.ripples.filter((ripple) => {
        ripple.radius += RIPPLE_GROWTH * this.bgMotion;
        ripple.life -= RIPPLE_DECAY * this.bgMotion;
        return ripple.life > 0;
      });
    },

    pushFromPointer(ship, shipX, shipY, px, py) {
      const dx = shipX - px;
      const dy = shipY - py;
      const distance = Math.sqrt(dx * dx + dy * dy) || 1;
      if (distance < FLEET.PUSH_RADIUS) {
        const force = (1 - distance / FLEET.PUSH_RADIUS) * FLEET.PUSH_FORCE * this.bgMotion;
        ship.velX += (dx / distance) * force;
        ship.velY += (dy / distance) * force;
      }
      return distance;
    },

    pushFromRipples(ship, shipX, shipY) {
      this.ripples.forEach((ripple) => {
        const rx = shipX - ripple.x;
        const ry = shipY - ripple.y;
        const rd = Math.sqrt(rx * rx + ry * ry) || 1;
        if (Math.abs(rd - ripple.radius) >= 64) return;
        const push = ripple.life * 0.55 * this.bgMotion;
        ship.velX += (rx / rd) * push;
        ship.velY += (ry / rd) * push;
      });
    },

    advanceFleet(sampling) {
      const px = this.pointerX * this.width;
      const py = this.pointerY * this.height;
      let leadDistance = FLEET.PUSH_RADIUS;
      this.lead = -1;

      this.ships.forEach((ship, index) => {
        ship.t += ship.speed * this.bgMotion;
        if (ship.t >= 1) {
          ship.t -= 1;
          ship.trail.length = 0;
        }

        const base = this.scaledPoint(ship.route, ship.t);
        const ahead = this.scaledPoint(ship.route, Math.min(1, ship.t + 0.004));
        const shipX = base.x + ship.offsetX;
        const shipY = base.y + ship.offsetY;

        if (this.pointerActive) {
          const distance = this.pushFromPointer(ship, shipX, shipY, px, py);
          if (distance < leadDistance) {
            leadDistance = distance;
            this.lead = index;
          }
        }
        this.pushFromRipples(ship, shipX, shipY);

        const next = stepShip(ship, this.bgMotion);
        ship.offsetX = next.offsetX;
        ship.offsetY = next.offsetY;
        ship.velX = next.velX;
        ship.velY = next.velY;

        ship.x = base.x + ship.offsetX;
        ship.y = base.y + ship.offsetY;
        ship.angle = Math.atan2(ahead.y - base.y + ship.velY, ahead.x - base.x + ship.velX);

        if (!sampling) return;
        ship.trail.push({ x: ship.x, y: ship.y });
        if (ship.trail.length > FLEET.TRAIL_MAX) ship.trail.shift();
      });
    },

    /* ---------- gambar ---------- */

    drawLanes() {
      const { ctx } = this;
      ctx.lineWidth = 1;
      ctx.strokeStyle = `rgba(${this.coolTint()}, ${this.ink(0.12)})`;
      ctx.setLineDash([4, 10]);
      this.lanePaths.forEach((path) => ctx.stroke(path));
      ctx.setLineDash([]);
    },

    drawLinks() {
      const { ctx, ships } = this;
      for (let i = 0; i < ships.length; i += 1) {
        for (let j = i + 1; j < ships.length; j += 1) {
          const dx = ships[i].x - ships[j].x;
          const dy = ships[i].y - ships[j].y;
          const fade = linkAlpha(Math.sqrt(dx * dx + dy * dy), this.linkDistance);
          if (fade > 0) {
            const hot = i === this.lead || j === this.lead;
            ctx.strokeStyle = hot
              ? `rgba(${this.hotTint()}, ${this.ink(fade * 0.62)})`
              : `rgba(${this.coolTint()}, ${this.ink(fade * 0.34)})`;
            ctx.lineWidth = hot ? 1.5 : 1;
            ctx.beginPath();
            ctx.moveTo(ships[i].x, ships[i].y);
            ctx.lineTo(ships[j].x, ships[j].y);
            ctx.stroke();
          }
        }
      }
    },

    // satu sapuan bergradasi per jejak, jauh lebih murah dari per-segmen
    strokeTrail(ship, from, tint, alpha, lineWidth) {
      const points = ship.trail;
      if (points.length - from < 2) return;
      const { ctx } = this;
      const head = points[from];
      const fade = ctx.createLinearGradient(head.x, head.y, ship.x, ship.y);
      fade.addColorStop(0, `rgba(${tint}, 0)`);
      fade.addColorStop(1, `rgba(${tint}, ${alpha})`);
      ctx.strokeStyle = fade;
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      ctx.moveTo(head.x, head.y);
      for (let i = from + 1; i < points.length; i += 1) ctx.lineTo(points[i].x, points[i].y);
      ctx.lineTo(ship.x, ship.y);
      ctx.stroke();
    },

    drawTrails() {
      const { ctx } = this;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      this.ships.forEach((ship, index) => {
        const hot = index === this.lead;
        const tint = hot ? this.hotTint() : this.wakeTint();
        const tail = Math.floor(ship.trail.length * 0.62);
        this.strokeTrail(ship, 0, tint, this.ink(hot ? 0.7 : 0.42), hot ? 2 : 1.3);
        // sapuan kedua cuma di ekor terdekat, bikin jejaknya menebal dekat kapal
        this.strokeTrail(ship, tail, tint, this.ink(hot ? 0.5 : 0.24), hot ? 5.5 : 3.4);
      });
    },

    drawShips() {
      const { ctx } = this;
      this.ships.forEach((ship, index) => {
        const hot = index === this.lead;
        const halo = hot ? 26 : 16;
        const glow = ctx.createRadialGradient(ship.x, ship.y, 0, ship.x, ship.y, halo);
        const core = hot ? this.hotTint() : this.wakeTint();
        glow.addColorStop(0, `rgba(${core}, ${this.ink(hot ? 0.55 : 0.32, this.glowScale)})`);
        glow.addColorStop(1, `rgba(${this.hotTint()}, 0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(ship.x, ship.y, halo, 0, TAU);
        ctx.fill();

        ctx.save();
        ctx.translate(ship.x, ship.y);
        ctx.rotate(ship.angle);
        ctx.fillStyle = hot
          ? `rgba(${this.hotTint()}, ${this.ink(1)})`
          : `rgba(${this.hullTint()}, ${this.ink(0.92)})`;
        ctx.beginPath();
        ctx.moveTo(7.5, 0);
        ctx.lineTo(-5, 3.6);
        ctx.lineTo(-3, 0);
        ctx.lineTo(-5, -3.6);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      });
    },

    drawRipples() {
      const { ctx } = this;
      this.ripples.forEach((ripple) => {
        ctx.lineWidth = 2.4;
        ctx.strokeStyle = `rgba(${this.hotTint()}, ${this.ink(ripple.life * 0.7)})`;
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius, 0, TAU);
        ctx.stroke();

        // dua cincin susulan bikin riaknya kebaca sebagai air, bukan lingkaran tunggal
        [[30, 0.4], [62, 0.2]].forEach((echo) => {
          const radius = ripple.radius - echo[0];
          if (radius <= 0) return;
          ctx.lineWidth = 1.2;
          ctx.strokeStyle = `rgba(${this.wakeTint()}, ${this.ink(ripple.life * echo[1])})`;
          ctx.beginPath();
          ctx.arc(ripple.x, ripple.y, radius, 0, TAU);
          ctx.stroke();
        });
      });
    },

    stepCanvas() {
      if (!this.ctx) return;
      const sampling = this.tick % FLEET.TRAIL_EVERY === 0;
      this.tick += 1;
      this.ctx.clearRect(0, 0, this.width, this.height);
      this.advanceRipples();
      this.advanceFleet(sampling);
      this.drawLanes();
      this.drawLinks();
      this.drawTrails();
      this.drawShips();
      this.drawRipples();
    },
  },
};
</script>

<style scoped>
.stage-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  background: var(--sea-base);
}

.scene {
  position: absolute;
  inset: 0;
  overflow: hidden;
  filter: brightness(var(--bg-bright)) saturate(var(--bg-sat));
  transition: filter 700ms cubic-bezier(0.4, 0, 0.2, 1);
}

.scene-routes {
  background: radial-gradient(
    118% 96% at 64% 12%,
    var(--sea-top) 0%,
    var(--sea-mid) 48%,
    var(--sea-deep) 100%
  );
}

.routes-glow {
  position: absolute;
  top: -22%;
  left: 34%;
  width: 58%;
  height: 72%;
  border-radius: 50%;
  filter: blur(74px);
  background: radial-gradient(circle, var(--glow-a) 0%, var(--glow-b) 48%, transparent 70%);
  animation: glow-drift 31s ease-in-out infinite alternate;
}

@keyframes glow-drift {
  from { transform: translate3d(-6%, -3%, 0) scale(1); }
  to   { transform: translate3d(8%, 6%, 0) scale(1.18); }
}

.fleet-canvas {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}

/* pembungkusnya kebagian parallax kursor, g di dalamnya kebagian gerak loop */
.wave-layer {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 200%;
  pointer-events: none;
}

.wave-layer svg { display: block; width: 100%; height: 100%; }
.wave-layer g { animation: wave-slide linear infinite; }

/* satu periode gelombang = 720 unit viewBox */
@keyframes wave-slide {
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(-720px, 0, 0); }
}

.wave-back {
  height: 48%;
  opacity: var(--wave-op-back);
  transform: translate3d(calc(var(--mx) * -34px), calc(var(--my) * -14px), 0);
}

.wave-mid {
  height: 36%;
  opacity: var(--wave-op-mid);
  transform: translate3d(calc(var(--mx) * -20px), calc(var(--my) * -8px), 0);
}

.wave-front {
  height: 26%;
  opacity: var(--wave-op-front);
  transform: translate3d(calc(var(--mx) * 26px), calc(var(--my) * 10px), 0);
}

.wave-back g  { animation-duration: 38s; }
.wave-mid g   { animation-duration: 27s; }
.wave-front g { animation-duration: 19s; }

/* pitanya lebih terang dari langit di tema gelap, lebih pekat di tema terang */
.wave-band { transition: fill 480ms ease; }
.wave-band--back { fill: var(--wave-back); }
.wave-band--mid { fill: var(--wave-mid); }
.wave-band--front { fill: var(--wave-front); }

/* sumber cahaya di satu sisi, kolam gelap tepat di tempat form berdiri */
.stage-light {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background:
    radial-gradient(62% 58% at var(--light-x) var(--light-y), var(--sl-glare) 0%, transparent 66%),
    radial-gradient(36% 32% at var(--light-x) var(--light-y), var(--sl-halo) 0%, transparent 74%),
    radial-gradient(
      var(--sl-pool-size) at var(--form-x) var(--form-y),
      var(--sl-pool-a) 0%,
      var(--sl-pool-b) var(--sl-pool-p1),
      var(--sl-pool-c) var(--sl-pool-p2),
      transparent var(--sl-pool-p3)
    ),
    linear-gradient(to top, var(--sl-floor-a) 0%, var(--sl-floor-b) 28%, transparent 54%),
    radial-gradient(130% 118% at 50% 44%, transparent 46%, var(--sl-vignette) 100%);
}

/* denyut sekali pas login gagal; nilainya disetir rAF, bukan keyframe */
.stage-pulse {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  opacity: var(--pulse);
  mix-blend-mode: var(--pulse-blend);
  background:
    radial-gradient(
      74% 66% at var(--form-x) var(--form-y),
      rgba(var(--pulse-rgb), var(--pulse-a1)) 0%,
      rgba(var(--pulse-rgb), 0) 70%
    ),
    linear-gradient(rgba(var(--pulse-rgb), var(--pulse-a2)), rgba(var(--pulse-rgb), var(--pulse-a2)));
}
</style>
